import {
  PURCHASE_ORDER_IMPORT_PAST_DELIVERY_FILTER,
  PurchaseOrderImportPreviewRow,
  PurchaseOrderImportReportRow,
  PurchaseOrderImportRowResult,
} from "../../types/purchase-order.type";
import { formatThaiShortDate } from "../../utils/date";
import { BRANCH_REQUIRED_RESULT, DUPLICATE_RESULT } from "./constants";

/**
 * ตัวช่วยล้วน ๆ ของการเลือกสาขาในขั้นตรวจของ W2
 *
 * ไฟล์จริงมี ~8,700 แถว ทุกอย่างในนี้จึงเป็น O(แถวที่เกี่ยวข้อง) ด้วยดัชนีที่สร้างครั้งเดียว
 * ต่อผลตรวจ ไม่ไล่ทั้งไฟล์ทุกครั้งที่ผู้ใช้เลือกสาขาให้หนึ่งแถว
 */

/** สาขาที่เลือกไว้ต่อแถว: row_no → company_id (ไม่มีคีย์ = ยังไม่เลือก) */
export type BranchChoiceMap = Record<number, string>;

/** ค่าตัวกรองผลตรวจที่แปลว่า "ไม่กรอง" */
export const ALL_RESULTS = "ALL";

export type ResultFilterValue =
  | PurchaseOrderImportRowResult
  | typeof ALL_RESULTS
  | typeof PURCHASE_ORDER_IMPORT_PAST_DELIVERY_FILTER;

/** ชุด row_no ของแถวเกินกำหนดส่งที่ HO ติ๊ก "นำเข้า" */
export type IncludedPastRowNos = ReadonlySet<number>;

/**
 * แถวนี้ถูกบันทึกเป็น PO ตอนยืนยันหรือไม่ · แถวซ้ำไม่ถูกบันทึกเสมอ และแถวเกินกำหนดส่ง
 * ถูกบันทึกเฉพาะที่ติ๊กนำเข้า (backend ข้ามแถวที่ไม่อยู่ใน include_past_row_nos)
 */
export const isRowSaved = (
  row: PurchaseOrderImportPreviewRow,
  includedPast: IncludedPastRowNos,
): boolean =>
  row.result !== DUPLICATE_RESULT &&
  (!row.past_delivery || includedPast.has(row.row_no));

/** แถวเกินกำหนดส่งที่ติ๊กนำเข้าได้ (แถวซ้ำไม่นับ เพราะไม่ถูกบันทึกอยู่แล้ว) */
export const isPastImportable = (row: PurchaseOrderImportPreviewRow): boolean =>
  row.past_delivery && row.result !== DUPLICATE_RESULT;

/** จำนวนแถวที่จะถูกบันทึก = ตัวเลขบนปุ่มยืนยัน */
export const countSavedRows = (
  rows: PurchaseOrderImportPreviewRow[],
  includedPast: IncludedPastRowNos,
): number => rows.filter((row) => isRowSaved(row, includedPast)).length;

/**
 * แถวเกินกำหนดส่งที่ไม่ได้ติ๊กนำเข้า ในรูปแถวของรายงาน "แถวที่ข้าม" (CSV เดียวกับแถวที่
 * อ่านไม่ได้) · ไม่มีชื่อชีตในผลตรวจ ตำแหน่งจึงบอกเป็นเลขแถว
 */
export const pastDeliverySkippedReportRows = (
  rows: PurchaseOrderImportPreviewRow[],
  includedPast: IncludedPastRowNos,
): PurchaseOrderImportReportRow[] =>
  rows
    .filter((row) => isPastImportable(row) && !includedPast.has(row.row_no))
    .map((row) => ({
      location: `แถว ${row.row_no}`,
      row_no: row.row_no,
      cell_text: [row.po_number, row.ship_to_code, row.ship_to_name]
        .filter(Boolean)
        .join(" · "),
      reason: `เกินกำหนดส่ง (วันส่ง ${
        formatThaiShortDate(row.delivery_date, { weekday: false }) ||
        row.delivery_date
      }) — ไม่ได้นำเข้า`,
    }));

/** ดัชนี store number ของแถว "ต้องเลือกสาขา" — ใช้เติมสาขาให้ร้านเดียวกัน */
export interface BranchRequiredStoreIndex {
  rowNosByStore: Map<string, number[]>;
  storeByRowNo: Map<number, string>;
}

/**
 * ดัชนีของแถว "ต้องเลือกสาขา" ที่จะถูกบันทึก · แถวเกินกำหนดส่งที่ไม่ได้ติ๊กนำเข้าไม่ต้อง
 * มีสาขา จึงไม่อยู่ในดัชนี — ไม่ถูกเติมสาขาให้ ไม่ถูกนับว่ายังไม่ระบุสาขา และไม่ถูกส่ง
 * ใน branch_choices
 */
export const buildBranchRequiredStoreIndex = (
  rows: PurchaseOrderImportPreviewRow[],
  includedPast: IncludedPastRowNos = new Set(),
): BranchRequiredStoreIndex => {
  const rowNosByStore = new Map<string, number[]>();
  const storeByRowNo = new Map<number, string>();

  rows.forEach((row) => {
    if (row.result !== BRANCH_REQUIRED_RESULT) return;
    if (!isRowSaved(row, includedPast)) return;
    storeByRowNo.set(row.row_no, row.ship_to_code);
    const rowNos = rowNosByStore.get(row.ship_to_code);
    if (rowNos) rowNos.push(row.row_no);
    else rowNosByStore.set(row.ship_to_code, [row.row_no]);
  });

  return { rowNosByStore, storeByRowNo };
};

export interface BranchChoiceUpdate {
  choices: BranchChoiceMap;
  /** จำนวนแถวร้านเดียวกันที่ถูกเติมสาขาให้ (ไม่นับแถวที่ผู้ใช้สั่งเอง) */
  autoFilledCount: number;
}

/**
 * เลือกสาขาให้แถวที่ผู้ใช้สั่ง แล้วเติมสาขาเดียวกันให้แถว "ต้องเลือกสาขา" อื่นที่ store
 * number เดียวกันและยังไม่มีคนเลือก — ร้านหนึ่งอยู่สาขาเดียว (ตัดสินโดยเจ้าของงาน)
 *
 * - แถวที่สั่งเองถูกเขียนทับเสมอ เพราะเป็นการกระทำที่ผู้ใช้ตั้งใจ
 * - แถวที่ถูกเติมให้ ต้องยังไม่มีสาขา — ห้ามทับสิ่งที่ผู้ใช้เลือกไว้แล้ว
 * - companyId ว่าง = ล้างค่าของแถวที่สั่ง และไม่แตะแถวอื่น
 * - แถวที่ไม่ใช่ "ต้องเลือกสาขา" ถูกข้าม (backend ปฏิเสธ choice ของแถวพวกนั้น)
 */
export const applyBranchChoice = (
  current: BranchChoiceMap,
  targetRowNos: readonly number[],
  companyId: string,
  index: BranchRequiredStoreIndex,
): BranchChoiceUpdate => {
  const choices: BranchChoiceMap = { ...current };
  const targets = targetRowNos.filter((rowNo) =>
    index.storeByRowNo.has(rowNo),
  );

  if (companyId === "") {
    targets.forEach((rowNo) => {
      delete choices[rowNo];
    });
    return { choices, autoFilledCount: 0 };
  }

  const targetSet = new Set(targets);
  const stores = new Set<string>();
  targets.forEach((rowNo) => {
    choices[rowNo] = companyId;
    const store = index.storeByRowNo.get(rowNo);
    if (store !== undefined) stores.add(store);
  });

  let autoFilledCount = 0;
  stores.forEach((store) => {
    (index.rowNosByStore.get(store) ?? []).forEach((rowNo) => {
      if (targetSet.has(rowNo) || current[rowNo] !== undefined) return;
      choices[rowNo] = companyId;
      autoFilledCount += 1;
    });
  });

  return { choices, autoFilledCount };
};

/** ตัดช่องว่างและตัวพิมพ์ ให้ "big c " กับ "Big C" ค้นเจอเหมือนกัน */
export const normalizePreviewSearch = (search: string): string =>
  search.trim().toLowerCase();

/**
 * กรองแถวตัวอย่างด้วยผลตรวจ + คำค้น · คำค้นเทียบกับ store number (ship_to_code)
 * ชื่อสถานที่จัดส่ง และเลข PO แบบ "มีคำนี้อยู่" · `search` ต้อง normalize แล้ว
 */
export const filterPreviewRows = (
  rows: PurchaseOrderImportPreviewRow[],
  resultFilter: ResultFilterValue,
  search: string,
): PurchaseOrderImportPreviewRow[] => {
  if (resultFilter === ALL_RESULTS && search === "") return rows;

  return rows.filter((row) => {
    if (resultFilter === PURCHASE_ORDER_IMPORT_PAST_DELIVERY_FILTER) {
      if (!row.past_delivery) return false;
    } else if (resultFilter !== ALL_RESULTS && row.result !== resultFilter) {
      return false;
    }
    if (search === "") return true;
    return (
      row.ship_to_code.toLowerCase().includes(search) ||
      (row.ship_to_name ?? "").toLowerCase().includes(search) ||
      row.po_number.toLowerCase().includes(search)
    );
  });
};
